// FontAwesome ikon değeri → <i> sınıf dizesi. Aşama 0.6'da FA'nın Vue 2 bileşeni (`font-awesome-icon`
// etiketi, @fortawesome paketi 2.0.10) kalktı; bu yardımcı onun `icon` prop'unu nasıl çözdüğünü
// (normalizeIconArgs → fontawesome-svg-core 6.2.1 parse.icon) taklit eder ki dinamik ikonlar
// (backend SmartDuo satır aksiyonları, menü, kartlar) bugünkü gibi görünsün:
// - `[prefix, name]` dizisi ve `{ prefix, iconName }` nesnesi → `fa-<stil> fa-<ad>`.
// - Dize: stil/aile belirteçleri ve ikon adı kalır; svg-core'un ayrılmış (RESERVED_CLASSES) sınıfları
//   (`fa-lg`, `fa-2x`, `fa-fw`, `fa-spin`…) ve FA dışı belirteçler düşer, çünkü `icon` prop'u onları
//   hiç uygulamıyordu (menü `fa-solid fa-house fa-lg` gönderir, ikon taban boyutta basılır).
// - Stil/aile belirteci yoksa svg-core'un varsayılanı olan solid eklenir.
// Boyut, dönme vb. şablonda açık sınıfla verilir (`class="fa-lg"`, `{ 'fa-spin': … }`).

const SHORT_PREFIX_TO_CLASS = {
  fas: 'fa-solid',
  far: 'fa-regular',
  fal: 'fa-light',
  fat: 'fa-thin',
  fad: 'fa-duotone',
  fab: 'fa-brands',
  fak: 'fa-kit',
  fass: 'fa-sharp fa-solid'
};

const STYLES = ['solid', 'regular', 'light', 'thin', 'duotone', 'brands', 'kit'];
const FAMILIES = ['classic', 'sharp'];

const STYLE_OR_FAMILY_TOKENS = new Set([
  'fa',
  ...Object.keys(SHORT_PREFIX_TO_CLASS),
  ...STYLES.map(style => `fa-${style}`),
  ...FAMILIES.map(family => `fa-${family}`)
]);

// fontawesome-svg-core 6.2.1 RESERVED_CLASSES (index.mjs), `fa-` öneki olmadan.
const RESERVED_NAMES = new Set([
  ...FAMILIES,
  ...STYLES,
  '2xs', 'xs', 'sm', 'lg', 'xl', '2xl', 'beat', 'border', 'fade', 'beat-fade', 'bounce',
  'flip-both', 'flip-horizontal', 'flip-vertical', 'flip', 'fw', 'inverse', 'layers-counter',
  'layers-text', 'layers', 'li', 'pull-left', 'pull-right', 'pulse', 'rotate-180', 'rotate-270',
  'rotate-90', 'rotate-by', 'shake', 'spin-pulse', 'spin-reverse', 'spin', 'stack-1x', 'stack-2x',
  'stack', 'ul', 'duotone-group', 'swap-opacity', 'primary', 'secondary',
  ...Array.from({ length: 10 }, (_, index) => `${index + 1}x`),
  ...Array.from({ length: 20 }, (_, index) => `w-${index + 1}`)
]);

// svg-core ICON_SELECTION_SYNTAX_PATTERN: `fa-` içermeyen dize de bu kalıpla sınıf dizesi sayılır.
const ICON_SELECTION_SYNTAX_PATTERN = /fa(s|r|l|t|d|b|k|ss)?[- ]/;

function styleClass(prefix) {
  if (SHORT_PREFIX_TO_CLASS[prefix]) return SHORT_PREFIX_TO_CLASS[prefix];
  if (prefix === 'fa') return 'fa-solid';
  if (STYLES.includes(prefix)) return `fa-${prefix}`;
  if (STYLE_OR_FAMILY_TOKENS.has(prefix)) return prefix;
  return prefix || 'fa-solid';
}

function fromPrefixAndName(prefix, name) {
  if (typeof name !== 'string' || name === '') return '';
  const iconName = name.indexOf('fa-') === 0 ? name.slice(3) : name;
  return iconName ? `${styleClass(prefix)} fa-${iconName}` : '';
}

export function faIcon(icon) {
  if (Array.isArray(icon)) {
    return icon.length === 2 ? fromPrefixAndName(icon[0], icon[1]) : '';
  }
  if (icon && typeof icon === 'object') {
    return fromPrefixAndName(icon.prefix, icon.iconName);
  }
  if (typeof icon !== 'string') return '';

  const value = icon.trim();
  if (value === '') return '';
  if (value.indexOf('fa-') === -1 && !ICON_SELECTION_SYNTAX_PATTERN.test(value)) {
    return `fa-solid fa-${value}`;
  }

  const kept = [];
  let hasStyle = false;
  let hasName = false;
  for (const token of value.split(/\s+/)) {
    if (STYLE_OR_FAMILY_TOKENS.has(token)) {
      hasStyle = true;
      kept.push(token);
    } else if (token.indexOf('fa-') === 0 && token.length > 3 && !RESERVED_NAMES.has(token.slice(3))) {
      hasName = true;
      kept.push(token);
    }
  }
  if (!hasName) return '';
  return (hasStyle ? kept : ['fa-solid', ...kept]).join(' ');
}

// Vue 2: Vue.use(plugin) → prototype; Vue 3: app.use(plugin) → config.globalProperties.
export default {
  install(app) {
    const target = app.config && app.config.globalProperties ? app.config.globalProperties : app.prototype;
    target.$faIcon = faIcon;
  }
};
